import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, PencilLine, ShieldCheck, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	ensureDefaultPermissionTags,
	PROFESSIONAL_PERMISSION_TAG_OPTIONS,
} from '@/utils/professionalPermissionTags';

interface PermissionTagsEditorProps {
	value: Professionals.PermissionTag[];
	onSave: (permissionTags: Professionals.PermissionTag[]) => void;
	disabled?: boolean;
}

interface EditPermissionTagsModalProps {
	isOpen: boolean;
	value: Professionals.PermissionTag[];
	disabled?: boolean;
	onClose: () => void;
	onSave: (permissionTags: Professionals.PermissionTag[]) => void;
}

const EditPermissionTagsModal = ({
	isOpen,
	value,
	disabled = false,
	onClose,
	onSave,
}: EditPermissionTagsModalProps) => {
	const [draftTags, setDraftTags] = useState<Professionals.PermissionTag[]>(
		ensureDefaultPermissionTags(value),
	);

	useEffect(() => {
		if (isOpen) {
			setDraftTags(ensureDefaultPermissionTags(value));
		}
	}, [isOpen, value]);

	useEffect(() => {
		if (!isOpen) return;

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen]);

	if (!isOpen) return null;

	const handleToggle = (
		tag: Professionals.PermissionTag,
		enabled: boolean,
	) => {
		if (tag === 'DEFAULT') return;

		setDraftTags((current) => {
			const without = current.filter((currentTag) => currentTag !== tag);
			const next = enabled ? [...without, tag] : without;
			return ensureDefaultPermissionTags(next);
		});
	};

	const handleSave = () => {
		onSave(ensureDefaultPermissionTags(draftTags));
		onClose();
	};

	const hasChanges =
		ensureDefaultPermissionTags(draftTags).join(',') !==
		ensureDefaultPermissionTags(value).join(',');

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
				aria-hidden
			/>
			<div
				className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] border border-zinc-100 bg-white shadow-2xl duration-200"
				onClick={(event) => event.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby="edit-permission-tags-title"
			>
				<div className="flex shrink-0 items-center justify-between gap-4 border-b border-zinc-100 bg-zinc-50/80 px-6 py-4">
					<button
						type="button"
						onClick={onClose}
						disabled={disabled}
						className="inline-flex items-center gap-1.5 text-sm font-bold text-stone-500 transition-colors hover:text-stone-800 disabled:opacity-50"
					>
						<X className="h-4 w-4" />
						Fechar
					</button>
					<button
						type="button"
						onClick={handleSave}
						disabled={disabled || !hasChanges}
						className="rounded-xl bg-zinc-900 px-5 py-2 text-sm font-bold text-white shadow-sm transition-all hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
					>
						Salvar
					</button>
				</div>

				<div className="flex-1 overflow-y-auto px-6 py-5">
					<div className="mb-5">
						<div className="mb-3 inline-flex items-center justify-center rounded-2xl bg-zinc-900 p-2.5 text-white shadow-sm">
							<ShieldCheck className="h-5 w-5" />
						</div>
						<h2
							id="edit-permission-tags-title"
							className="text-lg font-black text-stone-900"
						>
							Editar permissões
						</h2>
						<p className="mt-1 text-sm text-stone-500">
							Selecione as permissões especiais deste
							profissional.
						</p>
					</div>

					<div className="flex flex-col gap-2">
						{PROFESSIONAL_PERMISSION_TAG_OPTIONS.map((option) => {
							const isChecked = draftTags.includes(option.value);
							const isRequired = option.required === true;

							return (
								<label
									key={option.value}
									className={cn(
										'flex cursor-pointer items-start gap-3 rounded-2xl border border-solid px-4 py-3.5 transition-all',
										isChecked
											? 'border-zinc-900 bg-zinc-50 shadow-sm'
											: 'border-zinc-100 bg-white hover:border-zinc-200 hover:bg-zinc-50/80',
										isRequired && 'cursor-default',
										disabled &&
											'pointer-events-none opacity-60',
									)}
								>
									<span
										className={cn(
											'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-solid transition-all',
											isChecked
												? 'border-zinc-900 bg-zinc-900 text-white'
												: 'border-zinc-300 bg-white text-transparent',
										)}
									>
										<Check
											className="h-3.5 w-3.5"
											strokeWidth={3}
										/>
									</span>
									<input
										type="checkbox"
										checked={isChecked}
										disabled={disabled || isRequired}
										onChange={(event) =>
											handleToggle(
												option.value,
												event.target.checked,
											)
										}
										className="sr-only"
									/>
									<span className="min-w-0 flex-1">
										<span className="flex items-center gap-2">
											<span className="block text-sm font-bold text-zinc-900">
												{option.label}
											</span>
											{isRequired && (
												<span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-zinc-600">
													Obrigatória
												</span>
											)}
										</span>
										<span className="mt-1 block text-xs leading-relaxed text-zinc-500">
											{option.hint}
										</span>
									</span>
								</label>
							);
						})}
					</div>
				</div>
			</div>
		</div>,
		document.body,
	);
};

const PermissionTagsEditor = ({
	value,
	onSave,
	disabled = false,
}: PermissionTagsEditorProps) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const selectedTags = ensureDefaultPermissionTags(value);
	const grantedCount = selectedTags.length;

	const grantedCountSuffix = useMemo(
		() =>
			grantedCount === 1
				? 'permissão concedida'
				: 'permissões concedidas',
		[grantedCount],
	);

	return (
		<>
			<div className="flex items-center justify-between gap-4 rounded-2xl border border-solid border-zinc-100 bg-gradient-to-br from-zinc-50 via-white to-zinc-50 p-4 shadow-sm">
				<div className="flex min-w-0 items-center gap-4">
					<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-zinc-900 text-white shadow-md">
						<ShieldCheck className="h-5 w-5" />
					</div>
					<div className="min-w-0">
						<p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
							Permissões ativas
						</p>
						<div className="mt-0.5 flex items-baseline gap-2">
							<span className="text-3xl font-black leading-none text-zinc-900">
								{grantedCount}
							</span>
							<span className="truncate text-sm font-medium text-zinc-500">
								{grantedCountSuffix}
							</span>
						</div>
					</div>
				</div>

				<button
					type="button"
					onClick={() => setIsModalOpen(true)}
					disabled={disabled}
					className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-solid border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold text-zinc-900 shadow-sm transition-all hover:border-zinc-300 hover:bg-zinc-50 hover:shadow disabled:cursor-not-allowed disabled:opacity-60"
				>
					<PencilLine className="h-4 w-4" />
					<span className="hidden sm:inline">Editar permissões</span>
					<span className="sm:hidden">Editar</span>
				</button>
			</div>

			<EditPermissionTagsModal
				isOpen={isModalOpen}
				value={selectedTags}
				disabled={disabled}
				onClose={() => setIsModalOpen(false)}
				onSave={onSave}
			/>
		</>
	);
};

export { PermissionTagsEditor };
